import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import BackButton from '../../components/BackButton';
import { toast } from 'sonner';
import { Plus, Search, FileDown, Mail, Eye, Trash2, ArrowRightCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DOC_META = {
  quote: {
    title: 'Presupuestos', createPath: '/billing/quotes/new', detailPath: (id) => `/billing/quotes/${id}`,
    statuses: ['borrador','enviado','aceptado','rechazado','caducado'],
    statusColors: { borrador: 'bg-slate-100 text-slate-700', enviado: 'bg-blue-100 text-blue-800',
                    aceptado: 'bg-green-100 text-green-800', rechazado: 'bg-red-100 text-red-800',
                    caducado: 'bg-orange-100 text-orange-800' },
  },
  delivery_note: {
    title: 'Albaranes', createPath: '/billing/delivery-notes/new', detailPath: (id) => `/billing/delivery-notes/${id}`,
    statuses: ['borrador','entregado','facturado','anulado'],
    statusColors: { borrador: 'bg-slate-100 text-slate-700', entregado: 'bg-blue-100 text-blue-800',
                    facturado: 'bg-green-100 text-green-800', anulado: 'bg-red-100 text-red-800' },
  },
  invoice: {
    title: 'Facturas', createPath: '/billing/invoices/new', detailPath: (id) => `/billing/invoices/${id}`,
    statuses: ['borrador','emitida','parcial','pagada','vencida','anulada'],
    statusColors: { borrador: 'bg-slate-100 text-slate-700', emitida: 'bg-blue-100 text-blue-800',
                    parcial: 'bg-amber-100 text-amber-800', pagada: 'bg-green-100 text-green-800',
                    vencida: 'bg-red-100 text-red-800', anulada: 'bg-slate-200 text-slate-500' },
  },
};

const money = (v) => (v ?? 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
const fmtDate = (d) => d ? d.split('-').reverse().join('/') : '-';

const SalesDocList = ({ docType }) => {
  const meta = DOC_META[docType];
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterEstado, setFilterEstado] = useState('');

  useEffect(() => {
    fetchList();
     
  }, [docType, filterEstado]);

  const fetchList = async () => {
    try {
      setLoading(true);
      const params = { document_type: docType };
      if (filterEstado) params.estado = filterEstado;
      const res = await axios.get(`${API_URL}/billing/sales-documents`, { params });
      setItems(res.data);
    } catch (e) {
      toast.error('Error al cargar documentos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este borrador?')) return;
    try {
      await axios.delete(`${API_URL}/billing/sales-documents/${id}`);
      toast.success('Eliminado');
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const openPdf = (id) => {
    const token = localStorage.getItem('token');
    const url = `${API_URL}/billing/sales-documents/${id}/pdf`;
    // Descarga con auth
    axios.get(url, { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const blob = new Blob([r.data], { type: 'application/pdf' });
        const link = URL.createObjectURL(blob);
        window.open(link, '_blank');
      })
      .catch(() => toast.error('Error al descargar PDF'));
  };

  const convertToInvoice = async (id) => {
    try {
      const res = await axios.post(`${API_URL}/billing/sales-documents/${id}/convert-to-invoice`);
      toast.success('Convertido a factura (borrador)');
      navigate(`/billing/invoices/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al convertir');
    }
  };

  const filtered = items.filter(d => {
    if (!search) return true;
    const s = search.toLowerCase();
    return d.numero?.toLowerCase().includes(s) ||
           d.client_data?.nombre_fiscal?.toLowerCase().includes(s) ||
           d.client_data?.cif_nif?.toLowerCase().includes(s) ||
           d.referencia?.toLowerCase().includes(s);
  });

  return (
    <Layout>
      <div className="space-y-6" data-testid={`salesdoc-list-${docType}`}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <BackButton to="/billing" label="Volver a Intranet" />
            <h1 className="text-3xl font-manrope font-bold text-slate-900">{meta.title}</h1>
            <p className="text-slate-500 mt-1">Total: {items.length}</p>
          </div>
          <Button onClick={() => navigate(meta.createPath)} data-testid={`new-${docType}-button`} className="bg-[#004E89] hover:bg-[#003a68]">
            <Plus className="w-4 h-4 mr-2" /> Nuevo
          </Button>
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input className="pl-9" placeholder="Buscar por nº, cliente, CIF..." value={search} onChange={e=>setSearch(e.target.value)} />
          </div>
          <select value={filterEstado} onChange={e=>setFilterEstado(e.target.value)} className="rounded-md border border-slate-300 px-3 py-2 text-sm">
            <option value="">Todos los estados</option>
            {meta.statuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          {loading ? (
            <div className="p-12 text-center text-slate-500">Cargando...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-500">Sin documentos.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Nº</th>
                    <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Fecha</th>
                    <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Cliente</th>
                    <th className="text-right p-3 text-xs font-semibold text-slate-700 uppercase">Base imp.</th>
                    <th className="text-right p-3 text-xs font-semibold text-slate-700 uppercase">Total</th>
                    {docType === 'invoice' && (
                      <>
                        <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Vto.</th>
                        <th className="text-right p-3 text-xs font-semibold text-slate-700 uppercase">Pagado</th>
                      </>
                    )}
                    <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Estado</th>
                    <th className="text-right p-3 text-xs font-semibold text-slate-700 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(d => {
                    const vencida = docType === 'invoice' && d.fecha_vencimiento && d.fecha_vencimiento < new Date().toISOString().slice(0,10) && !['pagada','anulada','borrador'].includes(d.estado);
                    return (
                      <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-3 text-sm font-medium text-slate-900 font-mono">{d.numero}</td>
                        <td className="p-3 text-sm text-slate-600">{fmtDate(d.fecha_emision)}</td>
                        <td className="p-3 text-sm text-slate-700">
                          {d.client_data?.nombre_fiscal || '-'}
                          <div className="text-xs text-slate-500">{d.client_data?.cif_nif}</div>
                        </td>
                        <td className="p-3 text-sm text-right text-slate-600">{money(d.base_imponible)}</td>
                        <td className="p-3 text-sm text-right font-medium text-slate-900">{money(d.total)}</td>
                        {docType === 'invoice' && (
                          <>
                            <td className={`p-3 text-sm ${vencida ? 'text-red-600 font-medium' : 'text-slate-600'}`}>{fmtDate(d.fecha_vencimiento)}</td>
                            <td className="p-3 text-sm text-right text-slate-600">{money(d.total_pagado || 0)}</td>
                          </>
                        )}
                        <td className="p-3">
                          <span className={`inline-block text-xs px-2 py-1 rounded-full capitalize ${meta.statusColors[d.estado] || 'bg-slate-100'}`}>
                            {d.estado}
                          </span>
                        </td>
                        <td className="p-3 text-right space-x-1">
                          <button onClick={()=>navigate(meta.detailPath(d.id))} title="Ver/Editar" className="p-1 text-slate-600 hover:text-blue-600"><Eye className="w-4 h-4 inline" /></button>
                          <button onClick={()=>openPdf(d.id)} title="PDF" className="p-1 text-slate-600 hover:text-blue-600"><FileDown className="w-4 h-4 inline" /></button>
                          {docType === 'quote' && d.estado === 'enviado' && !d.factura_generada_id && (
                            <button onClick={()=>convertToInvoice(d.id)} title="Convertir a factura" className="p-1 text-slate-600 hover:text-green-600"><ArrowRightCircle className="w-4 h-4 inline" /></button>
                          )}
                          {docType === 'delivery_note' && d.estado === 'entregado' && !d.factura_generada_id && (
                            <button onClick={()=>convertToInvoice(d.id)} title="Convertir a factura" className="p-1 text-slate-600 hover:text-green-600"><ArrowRightCircle className="w-4 h-4 inline" /></button>
                          )}
                          {d.estado === 'borrador' && (
                            <button onClick={()=>handleDelete(d.id)} title="Eliminar borrador" className="p-1 text-slate-600 hover:text-red-600"><Trash2 className="w-4 h-4 inline" /></button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default SalesDocList;
